"use client";

import React from "react";
import { ArrowRight, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function BlogGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage
  } = usePosts();
  if (loading) {
    return <div data-section-id="128621" className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border-border/50 shadow-2xs">
            <div className="aspect-[16/9] w-full animate-pulse bg-muted" />
            <CardHeader className="pb-4">
              <div className="mb-2 h-4 w-24 animate-pulse rounded-md bg-muted" />
              <div className="h-6 w-full animate-pulse rounded-md bg-muted" />
              <div className="mt-2 h-4 w-2/3 animate-pulse rounded-md bg-muted" />
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="h-4 w-full animate-pulse rounded-md bg-muted" />
                <div className="h-4 w-4/5 animate-pulse rounded-md bg-muted" />
              </div>
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-lg border border-destructive/20 bg-destructive/5 p-8 text-center">
        <AlertCircle className="mb-4 size-8 text-destructive" />
        <p className="mb-4 text-sm text-muted-foreground">
          Não foi possível carregar os artigos técnicos no momento.
        </p>
        <Button variant="outline" onClick={refetch} className="border-primary text-primary hover:bg-primary/5">
          Tentar novamente
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-lg border border-border/50 bg-muted/20 p-12 text-center">
        <p className="text-muted-foreground">Nenhum conteúdo técnico disponível no momento.</p>
      </div>;
  }
  return <div className="space-y-12">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => {
        const category = post.categories?.[0]?.name;
        const excerpt = post.excerpt?.replace(/(<([^>]+)>)/gi, "") || "";
        return <Card key={post.id} data-index={i} className="group flex h-full flex-col overflow-hidden border-border bg-card shadow-2xs transition-all duration-300 hover:border-primary/30">
              <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted">
                <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/blog_placeholder_generic.png"} alt={post.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                {category && <Badge variant="secondary" className="absolute left-4 top-4 bg-background/95 text-secondary font-mono text-[10px] uppercase tracking-wider border-border backdrop-blur-sm hover:bg-background/95">
                    {category}
                  </Badge>}
              </div>

              <CardHeader className="flex-1 pb-4">
                <Link to={post.link || "#"} className="block">
                  <CardTitle className="font-default text-xl font-bold leading-tight tracking-tight transition-colors group-hover:text-primary line-clamp-2" dangerouslySetInnerHTML={{
                __html: post.title
              }} />
                </Link>
                {excerpt && <CardDescription className="mt-3 line-clamp-3 text-sm leading-relaxed text-muted-foreground font-sans">
                    {excerpt}
                  </CardDescription>}
              </CardHeader>

              <CardFooter className="mt-auto pt-0">
                <Link to={post.link || "#"} className="inline-flex items-center text-sm font-bold text-primary transition-colors hover:text-primary-600">
                  Ler artigo completo
                  <ArrowRight className="ml-2 size-4 -translate-x-2 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100" />
                </Link>
              </CardFooter>
            </Card>;
      })}
      </div>

      {(hasNext || hasPrev) && <div className="flex items-center justify-center gap-4 pt-4">
          <Button variant="outline" onClick={prevPage} disabled={!hasPrev} className="w-32 border-secondary text-secondary hover:bg-secondary/5 font-default">
            Anterior
          </Button>
          <Button variant="outline" onClick={nextPage} disabled={!hasNext} className="w-32 border-secondary text-secondary hover:bg-secondary/5 font-default">
            Próxima
          </Button>
        </div>}
    </div>;
}
export default function BlogEInsights() {
  const wp_query_params = {
    per_page: 6,
    status: "publish",
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="conteúdo-técnico" className="bg-background py-20 text-foreground md:py-28">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 max-w-2xl md:mb-16">
          <h2 className="font-default text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl text-foreground">
            Conteúdo Técnico & Insights
          </h2>
          <p className="mt-4 text-lg text-muted-foreground font-sans">
            Exploração aprofundada de inovações tecnológicas e práticas clínicas no setor de saúde.
          </p>
        </div>

        <PostsProvider wp_query={{
        per_page: 6,
        status: ["publish"],
        orderby: "date",
        order: "desc"
      }}>
          <BlogGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}